<template>
  <span class="high-frequency-game-results-game-number">
    <span
      v-for="(digit, index) in digits"
      :key="index"
      class="digit-slot"
      :style="isChanged(index) ? { '--digit-delay': `${index * 40}ms` } : {}"
    >
      <Transition name="digit">
        <span :key="digit" class="digit-value">{{ digit }}</span>
      </Transition>
    </span>
  </span>
</template>

<script lang="ts" setup>
type HighFrequencyGameResultsGameNumberProps = {
  gameNumber?: string
}

const props = withDefaults(defineProps<HighFrequencyGameResultsGameNumberProps>(), {
  gameNumber: undefined,
})

const changedPositions = ref(new Set<number>())
const digits = computed(() => props.gameNumber?.split('') ?? [])

watch(
  () => props.gameNumber,
  (newVal, oldVal) => {
    const changed = new Set<number>()
    const len = Math.max(newVal?.length ?? 0, oldVal?.length ?? 0)
    for (let i = 0; i < len; i++) {
      if (newVal?.[i] !== oldVal?.[i]) changed.add(i)
    }
    changedPositions.value = changed
  },
)

const isChanged = (index: number) => changedPositions.value.has(index)
</script>

<style lang="scss" scoped>
.high-frequency-game-results-game-number {
  display: inline-flex;
  font-variant-numeric: tabular-nums;

  .digit-slot {
    position: relative;
    display: inline-block;
    overflow: hidden;
  }

  .digit-value {
    display: block;
  }

  .digit-enter-active,
  .digit-leave-active {
    transition:
      transform 0.3s cubic-bezier(0.25, 0.8, 0.4, 1),
      opacity 0.3s cubic-bezier(0.25, 0.8, 0.4, 1);
  }

  .digit-enter-active {
    transition-delay: var(--digit-delay, 0ms);
  }

  .digit-leave-active {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
  }

  .digit-enter-from {
    opacity: 0;
    transform: translateY(100%);
  }

  .digit-leave-to {
    opacity: 0;
    transform: translateY(-100%);
  }
}
</style>
